import { FC } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { v2_get_request_api } from '../../../../api/get'
import { createJob, updateJob } from './jobs-list/core/_requests'
import { KTCard, KTCardBody, QUERIES, isNotEmpty } from '../../../../_metronic/helpers'
import { PageTitle } from '../../../../_metronic/layout/core'
import { ArrowLeft } from 'lucide-react'
import TiptapEditor from '../../../../_component/TiptapEditor'
import AdditionalFieldsEditor from '../../../../_component/AdditionalFieldsEditor'
import { ListLoading } from '../../../../_component/ListLoading'

// ── Yup Validation Schema ──────────────────────────────────────────────
const jobValidationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').label('Title'),
  department: Yup.string().required('Department is required').label('Department'),
  location: Yup.string().required('Location is required').label('Location'),
  type: Yup.string().required('Job type is required').label('Job Type'),
  status: Yup.string().required('Status is required').label('Status'),
  content: Yup.string().required('Job content is required').label('Job Content'),
  additional_fields: Yup.array().label('Custom Application Questions'),
})

// ── Select Options ─────────────────────────────────────────────────────
const JOB_TYPE_OPTIONS = [
  { label: 'Full Time', value: 'FULL_TIME' },
  { label: 'Part Time', value: 'PART_TIME' },
  { label: 'Contract', value: 'CONTRACT' },
  { label: 'Remote', value: 'REMOTE' },
]

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Published', value: 'PUBLISHED' },
  { label: 'Closed', value: 'CLOSED' },
]

// ── Initial Values ─────────────────────────────────────────────────────
const defaultInitialValues = {
  title: '',
  department: '',
  location: '',
  type: '',
  status: '',
  content: '',
  additional_fields: [] as any[],
}

// ── Shared Field Styles ────────────────────────────────────────────────
const inputClass =
  'block w-full text-base py-2 px-4 bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500'
const labelClass = 'block mb-3 text-sm font-medium'
const requiredStar = "after:content-['*'] after:ml-1 after:text-red-500"

// ── Component ──────────────────────────────────────────────────────────
const JobEditor: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data, isInitialLoading: isLoading } = useQuery(
    [`${QUERIES.JOB_LIST}/${id}`],
    v2_get_request_api,
    { enabled: isEditing }
  )

  const existingJob = data?.data?.data || data?.data || data

  const initialValues = isEditing
    ? { ...defaultInitialValues, ...existingJob }
    : defaultInitialValues

  const onSuccess = () => {
    navigate('/jobs')
  }

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setSubmitting(true)
    try {
      if (isNotEmpty(values.id)) {
        await updateJob(values)
      } else {
        await createJob(values)
      }
      onSuccess()
    } catch (ex) {
      console.error(ex)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageTitle>{isEditing ? 'Edit Job' : 'Create New Job'}</PageTitle>

      <div className='mb-6 flex items-center justify-between'>
        <button
          onClick={() => navigate('/jobs')}
          className='flex items-center gap-2 text-gray-600 hover:text-[#008080] transition-colors font-medium'
        >
          <ArrowLeft className='w-4 h-4' />
          Back to Listings
        </button>
      </div>

      <KTCard>
        <KTCardBody>
          <div className='max-w-5xl mx-auto py-4'>
            <div className='mb-10'>
              <h2 className='text-2xl font-bold text-gray-800'>
                {isEditing ? 'Update Job Posting' : 'Write a New Job Posting'}
              </h2>
              <p className='text-gray-500 mt-1'>
                Fill in the details below. What you write in the content editor is exactly what
                applicants will see.
              </p>
            </div>

            <Formik
              enableReinitialize={true}
              validationSchema={jobValidationSchema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {(formik) => (
                <>
                  <Form className='space-y-8'>
                    <div className='grid grid-cols-12 gap-4'>
                      {/* ── Title ─────────────────────────────── */}
                      <div className='mb-5 lg:col-span-6'>
                        <label className={`${labelClass} ${requiredStar}`}>Title</label>
                        <Field
                          type='text'
                          name='title'
                          className={inputClass}
                          placeholder='e.g. Senior Software Engineer'
                        />
                        <div className='text-red-600 mt-2'>
                          <ErrorMessage name='title' />
                        </div>
                      </div>

                      {/* ── Slug (edit only, read-only) ───────── */}
                      {isEditing && (
                        <div className='mb-5 lg:col-span-6'>
                          <label className={labelClass}>Slug</label>
                          <Field
                            type='text'
                            name='slug'
                            className={`${inputClass} cursor-not-allowed opacity-60`}
                            disabled
                          />
                        </div>
                      )}

                      {/* ── Department ────────────────────────── */}
                      <div className='mb-5 lg:col-span-6'>
                        <label className={`${labelClass} ${requiredStar}`}>Department</label>
                        <Field
                          type='text'
                          name='department'
                          className={inputClass}
                          placeholder='e.g. Engineering'
                        />
                        <div className='text-red-600 mt-2'>
                          <ErrorMessage name='department' />
                        </div>
                      </div>

                      {/* ── Location ──────────────────────────── */}
                      <div className='mb-5 lg:col-span-6'>
                        <label className={`${labelClass} ${requiredStar}`}>Location</label>
                        <Field
                          type='text'
                          name='location'
                          className={inputClass}
                          placeholder='e.g. Lagos, Nigeria'
                        />
                        <div className='text-red-600 mt-2'>
                          <ErrorMessage name='location' />
                        </div>
                      </div>

                      {/* ── Job Type (Select) ─────────────────── */}
                      <div className='mb-5 lg:col-span-6'>
                        <label className={`${labelClass} ${requiredStar}`}>Job Type</label>
                        <Field as='select' name='type' className={inputClass}>
                          <option value=''>Select Job Type</option>
                          {JOB_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Field>
                        <div className='text-red-600 mt-2'>
                          <ErrorMessage name='type' />
                        </div>
                      </div>

                      {/* ── Status (Select) ───────────────────── */}
                      <div className='mb-5 lg:col-span-6'>
                        <label className={`${labelClass} ${requiredStar}`}>Status</label>
                        <Field as='select' name='status' className={inputClass}>
                          <option value=''>Select Status</option>
                          {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Field>
                        <div className='text-red-600 mt-2'>
                          <ErrorMessage name='status' />
                        </div>
                      </div>

                      {/* ── Content (TiptapEditor) ────────────── */}
                      <div className='mb-5 lg:col-span-12'>
                        <label className={`${labelClass} ${requiredStar}`}>Job Content</label>
                        <TiptapEditor
                          value={formik.values.content}
                          onChange={(html: string) => formik.setFieldValue('content', html)}
                        />
                        <div className='text-red-600 mt-2'>
                          <ErrorMessage name='content' />
                        </div>
                      </div>

                      {/* ── Custom Application Questions ──────── */}
                      <div className='mb-5 lg:col-span-12'>
                        <label className={labelClass}>Custom Application Questions</label>
                        <AdditionalFieldsEditor
                          value={formik.values.additional_fields || []}
                          onChange={(val: any) => formik.setFieldValue('additional_fields', val)}
                        />
                      </div>
                    </div>

                    {/* ── Validation Summary ──────────────────── */}
                    {Object.keys(formik.errors).length > 0 && formik.dirty && (
                      <div
                        className='p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50'
                        role='alert'
                      >
                        <div className='font-medium text-red-700'>
                          Please fix the following issues:
                        </div>
                        <ul className='mt-1.5 list-disc list-inside text-red-600'>
                          {Object.entries(formik.errors).map(([key, error]) => (
                            <li key={key}>
                              <strong>{key}</strong>:{' '}
                              {typeof error === 'string' ? error : 'Check this section'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* ── Actions ─────────────────────────────── */}
                    <div className='text-center pt-10 flex flex-wrap justify-center gap-3'>
                      <button
                        type='reset'
                        onClick={() => navigate('/jobs')}
                        className='inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition'
                        disabled={formik.isSubmitting || isLoading}
                      >
                        Discard
                      </button>
                      <button
                        type='submit'
                        disabled={isLoading || formik.isSubmitting || !formik.isValid}
                        className={`
                          relative inline-flex items-center justify-center px-6 py-2
                          rounded-md font-medium text-white transition-colors
                          bg-primary hover:bg-teal-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                        `}
                      >
                        <span className={formik.isSubmitting ? 'invisible' : ''}>Submit</span>

                        {formik.isSubmitting && (
                          <span className='absolute inset-0 flex items-center justify-center gap-2'>
                            <span>Please wait...</span>
                            <svg
                              className='w-5 h-5 animate-spin text-white'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                            >
                              <circle
                                className='opacity-25'
                                cx='12'
                                cy='12'
                                r='10'
                                stroke='currentColor'
                                strokeWidth='4'
                              ></circle>
                              <path
                                className='opacity-75'
                                fill='currentColor'
                                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                              ></path>
                            </svg>
                          </span>
                        )}
                      </button>
                    </div>
                  </Form>

                  {(formik.isSubmitting || isLoading) && <ListLoading />}
                </>
              )}
            </Formik>
          </div>
        </KTCardBody>
      </KTCard>
    </>
  )
}

export default JobEditor
